import { useState, useEffect } from 'react';
import {
  Flag,
  Plus,
  Trash2,
  RefreshCw,
  Globe,
  Building2,
  Search,
  Filter,
} from 'lucide-react';
import { useFeatureFlags } from '../../../hooks/platform-admin/useFeatureFlags';
import { FeatureFlag } from '../../../services/platform-admin/feature-flags.service';
import CreateFeatureFlagModal from './modals/CreateFeatureFlagModal';
import ManageOrganizationsModal from './modals/ManageOrganizationsModal';

export default function FeatureFlagsPage() {
  const { getAllFlags, deleteFlag, isLoading, error } = useFeatureFlags();

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [filteredFlags, setFilteredFlags] = useState<FeatureFlag[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [showManageModal, setShowManageModal] = useState(false);

  const loadFlags = async () => {
    const result = await getAllFlags();
    if (result) {
      setFlags(result);
      setFilteredFlags(result);
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  useEffect(() => {
    let filtered = flags;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (flag) =>
          flag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flag.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flag.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter((flag) => flag.category === categoryFilter);
    }

    setFilteredFlags(filtered);
  }, [searchTerm, categoryFilter, flags]);

  const handleDelete = async (flag: FeatureFlag) => {
    if (window.confirm(`Delete feature flag "${flag.name}"? This will remove it from all organizations.`)) {
      const success = await deleteFlag(flag.id);
      if (success) {
        await loadFlags();
      }
    }
  };

  const handleManage = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    setShowManageModal(true);
  };

  const categories = Array.from(new Set(flags.map((f) => f.category))).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feature Flags</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage features globally or per-organization
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadFlags}
            disabled={isLoading}
            className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
          >
            <Plus className="h-4 w-4" />
            Create Flag
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search flags..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-600">Total Flags</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{flags.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-600">Global Flags</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {flags.filter((f) => f.isGlobal).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-600">Org-Specific</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {flags.filter((f) => !f.isGlobal).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-600">Categories</div>
          <div className="text-2xl font-bold text-purple-600 mt-1">{categories.length}</div>
        </div>
      </div>

      {/* Flags List */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Flag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scope
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organizations
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading && flags.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    Loading feature flags...
                  </td>
                </tr>
              ) : filteredFlags.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No feature flags found
                  </td>
                </tr>
              ) : (
                filteredFlags.map((flag) => (
                  <tr key={flag.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        <Flag className="h-5 w-5 text-orange-600 mt-0.5" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{flag.name}</div>
                          {flag.description && (
                            <div className="text-xs text-gray-500 mt-1 max-w-md">
                              {flag.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded font-mono">
                        {flag.key}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {flag.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {flag.isGlobal ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          <Globe className="h-3 w-3" />
                          Global
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                          <Building2 className="h-3 w-3" />
                          Per-Org
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {flag.isGlobal ? (
                        <span className="text-gray-500">All</span>
                      ) : (
                        <button
                          onClick={() => handleManage(flag)}
                          className="text-orange-600 hover:text-orange-900 font-medium"
                        >
                          {flag._count?.organizations || 0}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {!flag.isGlobal && (
                          <button
                            onClick={() => handleManage(flag)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Manage Organizations"
                          >
                            <Building2 className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(flag)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateFeatureFlagModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadFlags();
          }}
        />
      )}

      {showManageModal && selectedFlag && (
        <ManageOrganizationsModal
          flag={selectedFlag}
          onClose={() => {
            setShowManageModal(false);
            setSelectedFlag(null);
          }}
          onSuccess={() => {
            setShowManageModal(false);
            setSelectedFlag(null);
            loadFlags();
          }}
        />
      )}
    </div>
  );
}
